import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { SocketProvider } from './SocketContext';
import { ThemeProvider } from './ThemeContext';
import { ToastProvider } from './components/ui/Toast';
import { ErrorBoundary, ErrorState } from './components/ui';
import AppShell from './components/shell/AppShell';

// Pages that never get the shell: sign-in, registration and email verification; and the admin
// area, which has its own sign-in and frame (src/admin) whatever account the page is signed in as.
const isPublicPath = (p) => p === '/' || p === '/login' || p === '/sign-up' || p.startsWith('/verify-email')
    || p === '/admin' || p.startsWith('/admin/');

/**
 * A page that fails to render shows this in its place, inside the shell, so the menu still
 * works; going to another page clears it (security audit 2026-10-18, M2).
 */
function PageError({ reset, inShell }) {
    return (
        <div className="eg-page">
            <ErrorState kicker="Something went wrong" title="This page couldn’t be shown" onRetry={reset} retryLabel="Try again" />
        </div>
    );
}

function Page({ pathname, inShell }) {
    return (
        <ErrorBoundary resetKey={pathname} fallback={({ reset }) => <PageError reset={reset} inShell={inShell} />}>
            <Outlet />
        </ErrorBoundary>
    );
}

function Layout() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // Handle hash-based URLs from legacy email links (e.g. /#/verify-email/verify/:token)
    useEffect(() => {
        const hash = window.location.hash;
        if (hash && hash.startsWith('#/')) {
            const path = hash.slice(1);
            window.location.hash = '';
            navigate(path, { replace: true });
        }
    }, [navigate]);

    if (!isAuthenticated || isPublicPath(pathname)) return <Page pathname={pathname} />;
    return (
        <AppShell>
            <Page pathname={pathname} inShell />
        </AppShell>
    );
}

function Root() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <ToastProvider>
                    <SocketProvider>
                        <Layout />
                    </SocketProvider>
                </ToastProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default Root;
